import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card, Label } from '../../design-system';
import { resolveTenant, TenantPublicInfo } from '../../services/consentApi';
import { 
  getConsentDetail,
  createWithdrawalRequest,
  getPortalToken, 
  getPortalUser, 
  isPortalSessionValid,
  clearPortalSession,
  ConsentDetail 
} from '../../services/portalApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

/**
 * Data Owner Withdrawal Request Form
 * Route: /t/:tenantSlug/my-consent/withdraw/:consentId
 */
const DataOwnerWithdrawalForm: React.FC = () => {
  const { tenantSlug, consentId } = useParams<{ tenantSlug: string; consentId: string }>();
  const navigate = useNavigate();
  const { t, language } = useI18n();

  // State
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  const [consent, setConsent] = useState<ConsentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // Check session and load data
  useEffect(() => {
    const loadData = async () => {
      if (!isPortalSessionValid()) {
        clearPortalSession();
        navigate(`/t/${tenantSlug}/my-consent/login`);
        return;
      }

      const token = getPortalToken();
      const user = getPortalUser();
      if (!token || !user || !consentId) {
        navigate(`/t/${tenantSlug}/my-consent/history`);
        return;
      }

      try {
        const [tenantInfo, consentData] = await Promise.all([
          resolveTenant(tenantSlug!),
          getConsentDetail(token, user.tenantId, user.dataOwnerId, consentId),
        ]);
        
        setTenant(tenantInfo);
        setConsent(consentData);

        // Can't withdraw if already withdrawn or expired
        if (consentData.status !== 'ACTIVE') {
          setError(t.portal?.cannotWithdraw || 'This consent cannot be withdrawn');
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          clearPortalSession();
          navigate(`/t/${tenantSlug}/my-consent/login`);
          return;
        }
        setError(err.response?.data?.message || 'Failed to load consent details');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantSlug, consentId, navigate, t]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!confirmed) {
      setError(t.portal?.pleaseConfirmWithdrawal || 'Please confirm that you understand the implications');
      return;
    }

    const token = getPortalToken();
    const user = getPortalUser();
    if (!token || !user || !consentId) {
      navigate(`/t/${tenantSlug}/my-consent/login`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await createWithdrawalRequest(token, user.tenantId, user.dataOwnerId, {
        consentId,
        reason: reason.trim() || undefined,
      });
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit withdrawal request');
    } finally {
      setSubmitting(false);
    }
  }, [confirmed, consentId, reason, tenantSlug, navigate, t]);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">{t.common.loading}</p>
      </main>
    );
  }

  // Success state
  if (success) {
    return (
      <main style={{ minHeight: '100vh', background: 'var(--ds-bg-secondary)' }} role="main">
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 16px' }}>
          <Card style={{ padding: 32, textAlign: 'center' }}>
            <div aria-hidden="true" style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'var(--ds-success-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: 40,
            }}>
              ✓
            </div>
            
            <h1 style={{ fontSize: 24, marginBottom: 16 }}>
              {t.portal?.withdrawalSubmitted || 'Withdrawal Request Submitted'}
            </h1>
            
            <p style={{ color: 'var(--ds-text-secondary)', marginBottom: 24, lineHeight: 1.6, fontSize: 16 }}>
              {t.portal?.withdrawalSubmittedDesc || 
                'Your withdrawal request has been submitted. You will be notified once it is processed by the organization.'}
            </p>

            <div style={{ 
              background: 'var(--ds-bg-secondary)', 
              padding: 16, 
              borderRadius: 8,
              marginBottom: 24,
            }}>
              <p style={{ fontSize: 16, margin: 0 }}>
                {t.portal?.processingTime || 'Processing time'}: <strong>7-30 {t.portal?.days || 'days'}</strong>
              </p>
            </div>

            <Button
              variant="primary"
              onClick={() => navigate(`/t/${tenantSlug}/my-consent/history`)}
              style={{ width: '100%', padding: '14px', fontSize: 16 }}
            >
              {t.portal?.backToHistory || 'Back to Consent History'}
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main style={{ minHeight: '100vh', background: 'var(--ds-bg-secondary)' }} role="main" aria-labelledby="page-title">
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="sr-only"
        style={{ position: 'absolute' }}
        onFocus={(e) => {
          e.currentTarget.classList.remove('sr-only');
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.left = '10px';
          e.currentTarget.style.padding = '12px 24px';
          e.currentTarget.style.background = 'var(--tashi-garnet-red-5)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.currentTarget.classList.add('sr-only');
        }}
      >
        Skip to main content
      </a>

      {/* Header */}
      <header style={{ 
        background: 'linear-gradient(135deg, var(--tashi-garnet-red-5) 0%, var(--tashi-garnet-red-7) 100%)', 
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <button
            onClick={() => navigate(`/t/${tenantSlug}/my-consent/history`)}
            aria-label={t.common?.back || 'Back to consent history'}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '2px solid transparent',
              fontSize: 18,
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: 8,
              color: 'white',
            }}
          >
            <span aria-hidden="true">←</span>
          </button>
          <div style={{ flex: 1 }}>
            <h1 id="page-title" style={{ fontSize: 18, margin: 0, fontWeight: 600, color: 'white' }}>
              {t.portal?.withdrawConsent || 'Withdraw Consent'}
            </h1>
            {tenant && (
              <p style={{ fontSize: 14, margin: 0, color: 'rgba(255,255,255,0.9)' }}>
                {tenant.name}
              </p>
            )}
          </div>
        </div>
      </header>

      <div id="main-content" style={{ maxWidth: 600, margin: '0 auto', padding: '24px' }}>
        {/* Error message - aria-live for screen readers */}
        <div aria-live="polite" aria-atomic="true">
          {error && (
            <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
        </div>

        {/* Consent details */}
        {consent && (
          <Card style={{ marginBottom: 20, padding: 20 }}>
            <h2 style={{ fontSize: 18, margin: '0 0 8px', fontWeight: 600 }}>
              {consent.projectName || consent.templateName}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ds-text-muted)', margin: 0 }}>
              {t.portal?.consentedOn || 'Consented on'}: {consent.submittedAt ? formatDate(consent.submittedAt) : 'N/A'}
            </p>
            
            {consent.templateName && consent.templateName !== consent.projectName && (
              <p style={{ fontSize: 14, marginTop: 12, marginBottom: 0, lineHeight: 1.5 }}>
                Template: {consent.templateName}
              </p>
            )}
          </Card>
        )}

        {/* Warning notice */}
        <Card 
          role="alert"
          style={{ 
            marginBottom: 16, 
            padding: 16, 
            background: 'var(--ds-warning-light)',
            border: '1px solid var(--ds-warning)',
          }}
        >
          <h2 style={{ fontSize: 16, margin: '0 0 12px', color: 'var(--ds-warning-dark)' }}>
            <span aria-hidden="true">⚠️</span> {t.portal?.importantNotice || 'Important Notice'}
          </h2>
          <ul style={{ 
            margin: 0, 
            paddingLeft: 20, 
            fontSize: 14, 
            lineHeight: 1.8,
            color: 'var(--ds-text)',
          }}>
            <li>{t.portal?.withdrawalNotice1 || 'The organization will stop processing your data for the stated purposes.'}</li>
            <li>{t.portal?.withdrawalNotice2 || 'Some data may be retained for legal compliance.'}</li>
            <li>{t.portal?.withdrawalNotice3 || 'You may need to provide consent again to use certain services.'}</li>
            <li>{t.portal?.withdrawalNotice4 || 'Processing time may take 7-30 days depending on applicable laws.'}</li>
          </ul>
        </Card>

        {/* Withdrawal form */}
        {consent?.status === 'ACTIVE' && (
          <form onSubmit={handleSubmit} aria-label="Withdrawal request form">
            <Card style={{ padding: 16 }}>
              {/* Reason (optional) */}
              <div style={{ marginBottom: 20 }}>
                <Label htmlFor="reason" style={{ fontSize: 16, marginBottom: 8, display: 'block' }}>
                  {t.portal?.withdrawalReason || 'Reason for withdrawal'} ({t.common.optional})
                </Label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t.portal?.withdrawalReasonPlaceholder || 'Tell us why you want to withdraw (optional)'}
                  rows={3}
                  aria-describedby="reason-hint"
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid var(--ds-border)',
                    fontSize: 16,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <p id="reason-hint" className="sr-only">
                  Providing a reason is optional but helps the organization understand your needs
                </p>
              </div>

              {/* Confirmation checkbox */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ 
                  display: 'flex', 
                  gap: 12, 
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    aria-describedby="confirm-hint"
                    style={{
                      width: 24,
                      height: 24,
                      marginTop: 2,
                      cursor: 'pointer',
                    }}
                  />
                  <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                    {t.portal?.withdrawalConfirmation || 
                      'I understand that withdrawing my consent may affect my ability to use certain services, and I wish to proceed with this request.'}
                  </span>
                </label>
                <p id="confirm-hint" className="sr-only">
                  You must check this box to submit the withdrawal request
                </p>
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                isLoading={submitting}
                disabled={submitting || !confirmed}
                aria-disabled={submitting || !confirmed}
                style={{ 
                  width: '100%', 
                  padding: '14px',
                  fontSize: 16,
                  background: 'var(--ds-error)',
                }}
              >
                {submitting 
                  ? t.common.loading 
                  : (t.portal?.submitWithdrawalRequest || 'Submit Withdrawal Request')
                }
              </Button>
            </Card>
          </form>
        )}

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link 
            to={`/t/${tenantSlug}/my-consent/history`}
            style={{ color: 'var(--ds-accent)', fontSize: 14 }}
          >
            {t.common.cancel}
          </Link>
        </div>
      </div>
    </main>
  );
};

export default DataOwnerWithdrawalForm;
